/**
 * ProgressStatTile — eine Kachel des Fortschritts-Bands (P4/B6b):
 * Label, Wert, optionaler Trend-Badge, optionale Sparkline (ab 2 Punkten).
 */
import type { ProgressPoint } from '@/lib/api/issues-insights'
import { cn } from '@/lib/utils'

interface ProgressStatTileProps {
  label: string
  value: string
  trend?: { text: string; positive: boolean } | null
  series?: ProgressPoint[]
  sparklineTestId?: string
  trendTestId?: string
}

function Sparkline({ points, testId }: { points: ProgressPoint[]; testId?: string }) {
  const w = 86, h = 30
  const values = points.map((p) => p.value)
  const min = Math.min(...values), max = Math.max(...values)
  const span = max - min || 1
  const coords = points
    .map((p, i) => `${(i / (points.length - 1)) * w},${h - 4 - ((p.value - min) / span) * (h - 8)}`)
    .join(' ')
  return (
    <svg width={w} height={h} aria-hidden="true" data-testid={testId}>
      <polyline points={coords} fill="none" strokeWidth="2" className="stroke-current text-neutral-400" />
    </svg>
  )
}

export function ProgressStatTile({ label, value, trend, series = [], sparklineTestId, trendTestId }: ProgressStatTileProps) {
  return (
    <div className="flex items-end justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div>
        <p className="text-xs font-medium uppercase text-neutral-400">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
          {value}
          {trend && (
            <span
              data-testid={trendTestId}
              className={cn(
                'ml-2 text-sm font-medium',
                trend.positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
              )}
            >
              {trend.text}
            </span>
          )}
        </p>
      </div>
      {series.length >= 2 && <Sparkline points={series} testId={sparklineTestId} />}
    </div>
  )
}
